import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OwnerPageLayout from '../../components/layout/OwnerPageLayout';
import { getPublicProfile, sendInquiry } from '../../services/marketplaceApi';
import '../../styles/marketplace.css';

const SITE_URL = 'https://sportprogress.ru';

function setMetaTag(attr, key, value) {
    if (!value) return;
    const selector = attr === 'property'
        ? `meta[property="${key}"]`
        : `meta[name="${key}"]`;
    let el = document.querySelector(selector);
    if (!el) {
        el = document.createElement('meta');
        if (attr === 'property') el.setAttribute('property', key);
        else el.setAttribute('name', key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', value);
}

export default function PublicProfilePage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation(['marketplace', 'common']);
    const [profile, setProfile] = useState(null);
    const [message, setMessage] = useState('');
    const [error, setError] = useState('');

    useEffect(() => {
        getPublicProfile(slug).then(setProfile).catch(() => setError(t('common:notFound')));
    }, [slug, t]);

    useEffect(() => {
        if (!profile) return;
        if (profile.isPublic === false) {
            navigate('/marketplace', { state: { reason: 'profile_private' } });
            return;
        }

        const title = `${profile.displayName} — SportProgress`;
        const description = profile.tagline
            || `${profile.displayName} на SportProgress — ${[profile.city, profile.region].filter(Boolean).join(', ')}`;
        const url = `${SITE_URL}/p/${profile.slug}`;

        document.title = title;
        setMetaTag('name', 'description', description);
        setMetaTag('property', 'og:title', profile.displayName);
        setMetaTag('property', 'og:description', description);
        setMetaTag('property', 'og:image', profile.photoUrl || '');
        setMetaTag('property', 'og:url', url);

        let canonical = document.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement('link');
            canonical.setAttribute('rel', 'canonical');
            document.head.appendChild(canonical);
        }
        canonical.setAttribute('href', url);
    }, [profile, navigate]);

    async function onInquiry(e) {
        e.preventDefault();
        if (!localStorage.getItem('token')) { window.location.href = '/login'; return; }
        await sendInquiry({ profileId: profile.id, inquiryType: 'train', message });
        setMessage('');
        alert(t('marketplace:inquirySent'));
    }

    if (error) return <OwnerPageLayout title={t('common:notFound')}><p>{error}</p><Link to="/marketplace">{t('marketplace:title')}</Link></OwnerPageLayout>;
    if (!profile) return <OwnerPageLayout title={t('common:loading')}><p>{t('common:loading')}</p></OwnerPageLayout>;

    return (
        <OwnerPageLayout title={profile.displayName} maxWidth={800} className="mp-page">
            {profile.coverPhotoUrl && <img src={profile.coverPhotoUrl} alt="" style={{ width: '100%', maxHeight: 200, objectFit: 'cover', borderRadius: 12, marginBottom: 16 }} />}
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                {profile.photoUrl && <img src={profile.photoUrl} alt="" style={{ width: 96, height: 96, borderRadius: '50%', objectFit: 'cover' }} />}
                <div>
                    <h1 style={{ margin: 0 }}>{profile.displayName} {profile.isVerified && <span title={t('marketplace:verified')}>✓</span>}</h1>
                    {profile.tagline && <p>{profile.tagline}</p>}
                    <p className="mp-card-location">📍 {[profile.city, profile.region, profile.country].filter(Boolean).join(', ')}</p>
                </div>
            </div>
            {profile.bio && <p style={{ marginTop: 16, lineHeight: 1.6 }}>{profile.bio}</p>}

            {profile.qualifications?.length > 0 && (
                <section style={{ marginTop: 20 }}>
                    <h3>{t('marketplace:profileEdit.tabs.qualifications')}</h3>
                    {profile.qualifications.map((q) => (
                        <div key={q.id} className="mp-list-card">{q.level?.name || q.level?.nameRu || q.customTitle}</div>
                    ))}
                </section>
            )}

            {profile.achievements?.length > 0 && (
                <section style={{ marginTop: 20 }}>
                    <h3>{t('marketplace:profileEdit.tabs.achievements')}</h3>
                    {profile.achievements.map((a) => (
                        <div key={a.id} className="mp-list-card">🏆 {a.title} — {a.result} ({a.year})</div>
                    ))}
                </section>
            )}

            <form onSubmit={onInquiry} className="sp-panel" style={{ marginTop: 24 }}>
                <h3>{t('marketplace:sendInquiry')}</h3>
                <textarea rows={4} value={message} onChange={(e) => setMessage(e.target.value)} required placeholder={t('marketplace:inquiryPlaceholder')} />
                <button type="submit" className="sp-btn sp-btn--primary" style={{ marginTop: 8 }}>{t('marketplace:sendInquiry')}</button>
            </form>
        </OwnerPageLayout>
    );
}
